// Per-dialect synthesizer for GRANT / REVOKE / DENY (v2 Slice F2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice F2.
//
// Per-engine surface:
//   - mssql:    GRANT priv … ON object TO principal [WITH GRANT OPTION]
//               REVOKE priv … FROM principal [CASCADE]
//               DENY priv … TO principal [CASCADE]
//   - postgres: GRANT { ALL PRIVILEGES | priv … } ON kind name TO role
//               [WITH GRANT OPTION]; ALTER DEFAULT PRIVILEGES …
//               REVOKE [GRANT OPTION FOR] priv … FROM role [CASCADE]
//   - mysql:    GRANT priv … ON db.tbl TO 'name'@'host' [WITH GRANT OPTION]
//               REVOKE priv … FROM 'name'@'host'
//   - oracle:   GRANT { sys_priv | obj_priv | role } TO grantee
//               [WITH ADMIN OPTION | WITH GRANT OPTION]
//               REVOKE { priv | role } FROM grantee
//   - sqlite:   not supported (no auth model).
//
// Common shape:
//   1) `formatPrivileges(privs, allShortcut?)` — joins privileges with
//      commas; expands ALL PRIVILEGES shortcut on supporting engines.
//   2) `formatObjectClause(engine, object)` — emits `ON <kind> <name>`.
//      Returns empty string for `kind: 'role'` (mssql) or `kind: 'system'`
//      (oracle) — those grant shapes have no ON clause.
//   3) `formatPrincipalList(engine, grantees)` — joins principals with
//      commas; handles mysql 'name'@'host' and PUBLIC.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { DenyDef, GrantDef, GrantObject, GrantPrincipal, RevokeDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { quoteMysqlPrincipal } from '../../../../core/utils/mysqlSqlLiteral';

const SQLITE_HINT = `sqlite has no auth model. Connection-level access is OS / file-permission driven.`;

function rejectIfSqlite(engine: SqlEngine, op: string): void {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support ${op} — ${SQLITE_HINT}`,
    );
  }
}

// ─── shared helpers ───────────────────────────────────────────────────────

function formatPrivileges(privs: readonly string[], allShortcut: boolean | undefined): string {
  if (allShortcut) return 'ALL PRIVILEGES';
  if (privs.length === 0) {
    throw new Error(`applyDdlEdit: grant/revoke requires a non-empty 'privileges' list `
      + `(or 'pgsqlAllPrivileges: true' on postgres).`);
  }
  return privs.join(', ');
}

function formatPrincipal(engine: SqlEngine, p: GrantPrincipal): string {
  if (p.kind === 'public') return 'PUBLIC';
  if (p.name === undefined || p.name.length === 0) {
    throw new Error(`applyDdlEdit: grant/revoke principal of kind '${p.kind}' requires 'name'.`);
  }
  if (engine === 'mysql' && p.kind === 'user') {
    return quoteMysqlPrincipal(p.name, p.mysqlHost);
  }
  return maybeQuoteIdent(engine, p.name, p.quoted ?? false);
}

function formatPrincipalList(engine: SqlEngine, ps: readonly GrantPrincipal[]): string {
  if (ps.length === 0) {
    throw new Error(`applyDdlEdit: grant/revoke requires at least one grantee.`);
  }
  return ps.map((p) => formatPrincipal(engine, p)).join(', ');
}

const OBJECT_KIND_KEYWORD: Record<string, string> = {
  table: 'TABLE',
  view: 'VIEW',
  sequence: 'SEQUENCE',
  schema: 'SCHEMA',
  database: 'DATABASE',
  function: 'FUNCTION',
  procedure: 'PROCEDURE',
  routine: 'ROUTINE',
  package: 'PACKAGE',
  type: 'TYPE',
  tablespace: 'TABLESPACE',
};

function formatObjectName(engine: SqlEngine, object: GrantObject): string {
  if (object.name === undefined || object.name.length === 0) {
    throw new Error(`applyDdlEdit: grant object of kind '${object.kind}' requires 'name'.`);
  }
  const name = maybeQuoteIdent(engine, object.name, object.quoted ?? false);
  if (object.schema !== undefined && object.schema.length > 0) {
    const schema = maybeQuoteIdent(engine, object.schema, object.quoted ?? false);
    return `${schema}.${name}`;
  }
  return name;
}

function formatMysqlObject(object: GrantObject): string {
  const quoteMysql = (name: string): string =>
    maybeQuoteIdent('mysql', name, object.quoted ?? false);
  if (object.kind === 'database') {
    if (object.name === undefined) {
      throw new Error(`applyDdlEdit: mysql grant on database requires 'name'.`);
    }
    return `${quoteMysql(object.name)}.*`;
  }
  if (object.kind === 'table') {
    if (object.mysqlAllInDatabase) {
      if (object.name === undefined) {
        throw new Error(`applyDdlEdit: mysql grant on database (mysqlAllInDatabase) requires 'name'.`);
      }
      return `${quoteMysql(object.name)}.*`;
    }
    if (object.name === undefined) {
      throw new Error(`applyDdlEdit: mysql grant on table requires 'name'.`);
    }
    if (object.schema !== undefined && object.schema.length > 0) {
      return `${quoteMysql(object.schema)}.${quoteMysql(object.name)}`;
    }
    return quoteMysql(object.name);
  }
  // mysql also supports FUNCTION / PROCEDURE qualifiers
  const kw = OBJECT_KIND_KEYWORD[object.kind];
  if (kw === undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mysql grant does not support object kind '${object.kind}'.`,
    );
  }
  if (object.name === undefined) {
    throw new Error(`applyDdlEdit: mysql grant on ${object.kind} requires 'name'.`);
  }
  if (object.schema !== undefined && object.schema.length > 0) {
    return `${kw} ${quoteMysql(object.schema)}.${quoteMysql(object.name)}`;
  }
  return `${kw} ${quoteMysql(object.name)}`;
}

function formatObjectClause(engine: SqlEngine, object: GrantObject): string {
  // mssql / pgsql / oracle: 'role' grants have no ON clause.
  if (object.kind === 'role') return '';
  // oracle system privileges: no ON clause; the privilege names *are* the
  // identity (e.g. CREATE SESSION).
  if (object.kind === 'system') {
    if (engine !== 'oracle') {
      throw new DdlEditError(
        'engine-does-not-support-op',
        `applyDdlEdit: ${engine} grant does not support 'kind: system' — `
        + `system privileges are an oracle-specific concept.`);
    }
    return '';
  }
  if (engine === 'mysql') return ` ON ${formatMysqlObject(object)}`;
  const kw = OBJECT_KIND_KEYWORD[object.kind];
  if (kw === undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} grant does not support object kind '${object.kind}'.`,
    );
  }
  // mssql uses class qualifiers `<CLASS>::<name>` for non-table/view kinds.
  // Tables and views use the default OBJECT class (no qualifier).
  if (engine === 'mssql') {
    if (object.kind === 'table' || object.kind === 'view') {
      return ` ON ${formatObjectName(engine, object)}`;
    }
    return ` ON ${kw}::${formatObjectName(engine, object)}`;
  }
  // oracle uses bare `ON <name>` for tables / views (no kind keyword); it
  // does not accept GRANT … ON SCHEMA / DATABASE / TABLESPACE shapes (those
  // are system privileges instead).
  if (engine === 'oracle') {
    if (object.kind === 'table' || object.kind === 'view'
        || object.kind === 'sequence' || object.kind === 'function'
        || object.kind === 'procedure' || object.kind === 'package'
        || object.kind === 'type') {
      return ` ON ${formatObjectName(engine, object)}`;
    }
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: oracle grant does not support 'kind: ${object.kind}' — `
      + `oracle expresses schema / database / tablespace privileges via system privileges. `
      + `Use 'kind: system' with a privilege name like 'CREATE TABLESPACE' instead.`);
  }
  // pgsql: emits the kind keyword for every kind.
  return ` ON ${kw} ${formatObjectName(engine, object)}`;
}

// ─── GRANT ────────────────────────────────────────────────────────────────

export function synthesizeGrant(engine: SqlEngine, grant: GrantDef): string {
  rejectIfSqlite(engine, 'grant');
  if (engine === 'mssql') return synthMssqlGrant(grant);
  if (engine === 'postgres') return synthPgsqlGrant(grant);
  if (engine === 'mysql') return synthMysqlGrant(grant);
  return synthOracleGrant(grant);
}

function synthMssqlGrant(grant: GrantDef): string {
  if (grant.pgsqlAllPrivileges || grant.pgsqlDefaultPrivileges !== undefined) {
    throw new Error(`applyDdlEdit: mssql grant does not accept 'pgsql*' fields — only postgres accepts these fields.`);
  }
  if (grant.oracleWithAdminOption) {
    throw new Error(`applyDdlEdit: mssql grant does not accept 'oracleWithAdminOption' — only oracle accepts this field.`);
  }
  const privs = formatPrivileges(grant.privileges, false);
  const obj = formatObjectClause('mssql', grant.object);
  const principals = formatPrincipalList('mssql', grant.grantees);
  let s = `GRANT ${privs}${obj} TO ${principals}`;
  if (grant.withGrantOption) s += ' WITH GRANT OPTION';
  if (grant.mssqlGrantedBy !== undefined) s += ` AS ${grant.mssqlGrantedBy}`;
  return s;
}

function synthPgsqlGrant(grant: GrantDef): string {
  if (grant.oracleWithAdminOption) {
    throw new Error(`applyDdlEdit: postgres grant does not accept 'oracleWithAdminOption' — only oracle accepts this field.`);
  }
  if (grant.mssqlGrantedBy !== undefined) {
    throw new Error(`applyDdlEdit: postgres grant does not accept 'mssqlGrantedBy' — only mssql accepts this field.`);
  }
  const privs = formatPrivileges(grant.privileges, grant.pgsqlAllPrivileges);
  const principals = formatPrincipalList('postgres', grant.grantees);
  if (grant.pgsqlDefaultPrivileges !== undefined) {
    const role = grant.pgsqlDefaultPrivileges.role;
    const schema = grant.pgsqlDefaultPrivileges.schema;
    let prefix = `ALTER DEFAULT PRIVILEGES FOR ROLE ${role}`;
    if (schema !== undefined && schema.length > 0) prefix += ` IN SCHEMA ${schema}`;
    const obj = formatObjectClause('postgres', grant.object);
    let s = `${prefix} GRANT ${privs}${obj} TO ${principals}`;
    if (grant.withGrantOption) s += ' WITH GRANT OPTION';
    return s;
  }
  const obj = formatObjectClause('postgres', grant.object);
  let s = `GRANT ${privs}${obj} TO ${principals}`;
  if (grant.withGrantOption) s += ' WITH GRANT OPTION';
  return s;
}

function synthMysqlGrant(grant: GrantDef): string {
  if (grant.pgsqlAllPrivileges || grant.pgsqlDefaultPrivileges !== undefined) {
    throw new Error(`applyDdlEdit: mysql grant does not accept 'pgsql*' fields — only postgres accepts these fields.`);
  }
  if (grant.oracleWithAdminOption) {
    throw new Error(`applyDdlEdit: mysql grant does not accept 'oracleWithAdminOption' — only oracle accepts this field.`);
  }
  if (grant.mssqlGrantedBy !== undefined) {
    throw new Error(`applyDdlEdit: mysql grant does not accept 'mssqlGrantedBy' — only mssql accepts this field.`);
  }
  const privs = formatPrivileges(grant.privileges, false);
  const obj = formatObjectClause('mysql', grant.object);
  const principals = formatPrincipalList('mysql', grant.grantees);
  let s = `GRANT ${privs}${obj} TO ${principals}`;
  if (grant.withGrantOption) s += ' WITH GRANT OPTION';
  return s;
}

function synthOracleGrant(grant: GrantDef): string {
  if (grant.pgsqlAllPrivileges || grant.pgsqlDefaultPrivileges !== undefined) {
    throw new Error(`applyDdlEdit: oracle grant does not accept 'pgsql*' fields — only postgres accepts these fields.`);
  }
  if (grant.mssqlGrantedBy !== undefined) {
    throw new Error(`applyDdlEdit: oracle grant does not accept 'mssqlGrantedBy' — only mssql accepts this field.`);
  }
  if (grant.withGrantOption && grant.oracleWithAdminOption) {
    throw new Error(`applyDdlEdit: oracle grant cannot set both 'withGrantOption' and 'oracleWithAdminOption' — they are mutually exclusive.`);
  }
  const privs = formatPrivileges(grant.privileges, false);
  const obj = formatObjectClause('oracle', grant.object);
  const principals = formatPrincipalList('oracle', grant.grantees);
  let s = `GRANT ${privs}${obj} TO ${principals}`;
  if (grant.oracleWithAdminOption) s += ' WITH ADMIN OPTION';
  else if (grant.withGrantOption) s += ' WITH GRANT OPTION';
  return s;
}

// ─── REVOKE ───────────────────────────────────────────────────────────────

export function synthesizeRevoke(engine: SqlEngine, revoke: RevokeDef): string {
  rejectIfSqlite(engine, 'revoke');
  if (engine === 'mssql') return synthMssqlRevoke(revoke);
  if (engine === 'postgres') return synthPgsqlRevoke(revoke);
  if (engine === 'mysql') return synthMysqlRevoke(revoke);
  return synthOracleRevoke(revoke);
}

function synthMssqlRevoke(revoke: RevokeDef): string {
  if (revoke.pgsqlGrantOptionFor || revoke.pgsqlAllPrivileges) {
    throw new Error(`applyDdlEdit: mssql revoke does not accept 'pgsql*' fields — only postgres accepts these fields.`);
  }
  const privs = formatPrivileges(revoke.privileges, false);
  const obj = formatObjectClause('mssql', revoke.object);
  const principals = formatPrincipalList('mssql', revoke.grantees);
  let s = `REVOKE ${privs}${obj} FROM ${principals}`;
  if (revoke.cascade) s += ' CASCADE';
  if (revoke.mssqlGrantedBy !== undefined) s += ` AS ${revoke.mssqlGrantedBy}`;
  return s;
}

function synthPgsqlRevoke(revoke: RevokeDef): string {
  if (revoke.mssqlGrantedBy !== undefined) {
    throw new Error(`applyDdlEdit: postgres revoke does not accept 'mssqlGrantedBy' — only mssql accepts this field.`);
  }
  const privs = formatPrivileges(revoke.privileges, revoke.pgsqlAllPrivileges);
  const obj = formatObjectClause('postgres', revoke.object);
  const principals = formatPrincipalList('postgres', revoke.grantees);
  const lead = revoke.pgsqlGrantOptionFor ? 'REVOKE GRANT OPTION FOR' : 'REVOKE';
  let s = `${lead} ${privs}${obj} FROM ${principals}`;
  if (revoke.cascade) s += ' CASCADE';
  return s;
}

function synthMysqlRevoke(revoke: RevokeDef): string {
  if (revoke.pgsqlGrantOptionFor || revoke.pgsqlAllPrivileges) {
    throw new Error(`applyDdlEdit: mysql revoke does not accept 'pgsql*' fields — only postgres accepts these fields.`);
  }
  if (revoke.mssqlGrantedBy !== undefined) {
    throw new Error(`applyDdlEdit: mysql revoke does not accept 'mssqlGrantedBy' — only mssql accepts this field.`);
  }
  if (revoke.cascade) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mysql revoke does not support 'cascade' — mysql REVOKE has no CASCADE clause.`,
    );
  }
  const privs = formatPrivileges(revoke.privileges, false);
  const obj = formatObjectClause('mysql', revoke.object);
  const principals = formatPrincipalList('mysql', revoke.grantees);
  return `REVOKE ${privs}${obj} FROM ${principals}`;
}

function synthOracleRevoke(revoke: RevokeDef): string {
  if (revoke.pgsqlGrantOptionFor || revoke.pgsqlAllPrivileges) {
    throw new Error(`applyDdlEdit: oracle revoke does not accept 'pgsql*' fields — only postgres accepts these fields.`);
  }
  if (revoke.mssqlGrantedBy !== undefined) {
    throw new Error(`applyDdlEdit: oracle revoke does not accept 'mssqlGrantedBy' — only mssql accepts this field.`);
  }
  if (revoke.cascade) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: oracle revoke does not support 'cascade' — oracle REVOKE has no CASCADE clause; use CASCADE CONSTRAINTS via DROP USER instead.`,
    );
  }
  const privs = formatPrivileges(revoke.privileges, false);
  const obj = formatObjectClause('oracle', revoke.object);
  const principals = formatPrincipalList('oracle', revoke.grantees);
  return `REVOKE ${privs}${obj} FROM ${principals}`;
}

// ─── DENY (mssql-only) ────────────────────────────────────────────────────

export function synthesizeDeny(engine: SqlEngine, deny: DenyDef): string {
  if (engine !== 'mssql') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support op:'deny' — DENY is mssql-specific. `
      + `Use op:'revoke' on ${engine} for the equivalent effect.`);
  }
  const privs = formatPrivileges(deny.privileges, false);
  const obj = formatObjectClause('mssql', deny.object);
  const principals = formatPrincipalList('mssql', deny.grantees);
  let s = `DENY ${privs}${obj} TO ${principals}`;
  if (deny.cascade) s += ' CASCADE';
  return s;
}
